/**
 * Pure SVG-path math for LgSparkline — extracted so the geometry is unit-
 * testable without a DOM. Index-based even x-spacing (a sparkline conveys
 * *shape*, not absolute time), y auto-scaled to the value range, inset by
 * half the stroke width so the line is never clipped at the edges.
 */

export interface SparkGeometry {
  /** SVG `d` attribute for the trend line (empty string when < 2 points). */
  line: string;
  /** SVG `d` attribute for the filled area under the line (closed path). */
  area: string;
  /** Resolved point coordinates, useful for markers/tests. */
  coords: Array<{ x: number; y: number }>;
}

/** Extract the numeric value from either a bare number or a `[ts, value]` tuple. */
export function toValues(points: number[] | Array<[number, number]>): number[] {
  return points.map((p) => (Array.isArray(p) ? p[1] : p));
}

/**
 * Compute the sparkline geometry for `values` inside a `width`×`height` box.
 * A single-value (or all-equal) series renders as a centered horizontal line.
 */
export function sparklineGeometry(
  values: number[],
  width: number,
  height: number,
  strokeWidth = 1.5,
): SparkGeometry {
  const empty: SparkGeometry = { line: '', area: '', coords: [] };
  if (!values.length || width <= 0 || height <= 0) return empty;

  const pad = strokeWidth / 2;
  const innerW = Math.max(0, width - strokeWidth);
  const innerH = Math.max(0, height - strokeWidth);
  const midY = height / 2;

  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  // No finite data at all — nothing to draw.
  if (min === Infinity) return empty;

  const span = max - min;
  const n = values.length;
  const stepX = n > 1 ? innerW / (n - 1) : 0;

  const coords = values.map((v, i) => {
    const x = pad + i * stepX;
    // Flat series (span 0) or a single point sits on the vertical midline.
    const y = span === 0 ? midY : pad + innerH - ((v - min) / span) * innerH;
    return { x: round(x), y: round(y) };
  });

  if (coords.length === 1) {
    // Draw a short horizontal tick so a one-sample sparkline still reads.
    const { y } = coords[0];
    return { line: `M${pad} ${y}L${width - pad} ${y}`, area: '', coords };
  }

  const baseY = round(height - pad);

  // Build the path from runs of consecutive *finite* points — a non-finite
  // interior sample must break the line into a gap (never draw through it,
  // never let a NaN/undefined coordinate leak into the path string). Runs
  // shorter than 2 points can't form a line segment and are dropped.
  let line = '';
  let area = '';
  let run: Array<{ x: number; y: number }> = [];
  const flushRun = () => {
    if (run.length >= 2) {
      const seg = run.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x} ${c.y}`).join('');
      const first = run[0];
      const last = run[run.length - 1];
      line += seg;
      area += `${seg}L${last.x} ${baseY}L${first.x} ${baseY}Z`;
    }
    run = [];
  };
  for (let i = 0; i < values.length; i++) {
    if (Number.isFinite(values[i])) {
      run.push(coords[i]);
    } else {
      flushRun();
    }
  }
  flushRun();

  return { line, area, coords };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
